import { Checkbox, Flex, Radio, Text, Divider, Tooltip } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import React from 'react';

type SelectableItem<S extends string | number> = {
  id: S;
  title: string;
  description?: string;
  right?: string;
  emptyView?: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
};

type Props<S extends string | number, T extends SelectableItem<S>> = {
  items: T[];
  hint?: string;
  selectedItemIds: S[];
  setSelectedItemIds: (itemIds: S[]) => void;
  selectMultiple?: boolean;
  emptyView?: React.ReactNode;
  renderExpandedContent?: (item: T) => React.ReactNode;
};

export default function SelectableItemList<S extends string | number, T extends SelectableItem<S>>({
  items,
  hint,
  selectedItemIds,
  setSelectedItemIds,
  selectMultiple,
  emptyView,
}: Props<S, T>) {
  const Selector = selectMultiple ? Checkbox : Radio;

  const handleSelectItem = (item: T) => {
    if (item.disabled) return;
    const isSelected = selectedItemIds.includes(item.id);
    if (isSelected) {
      setSelectedItemIds(selectedItemIds.filter(id => id !== item.id));
    } else {
      if (selectMultiple) {
        setSelectedItemIds([...selectedItemIds, item.id]);
      } else {
        setSelectedItemIds([item.id]);
      }
    }
  };

  return (
    <Flex direction="column" bg="gray.0" style={{ borderRadius: 12 }} mih={0} flex={1}>
      <Flex
        direction="column"
        gap="xs"
        p="md"
        flex={1}
        mih={0}
        bd="1px solid var(--mantine-color-gray-1)"
        style={{ borderRadius: 12, overflowY: 'auto', scrollbarWidth: 'thin' }}
        bg="white"
      >
        {items.length === 0 && emptyView}
        {items.map((item, index) => {
          const isSelected = selectedItemIds.includes(item.id);
          const isDisabled = item.disabled ?? false;
          const row = (
            <Flex gap="xs" opacity={isDisabled ? 0.5 : 1}>
              <Selector
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => handleSelectItem(item)}
                mt="xxxs"
              />
              <Flex direction={'row'} justify={'space-between'} align={'center'} flex={1}>
                <Flex
                  flex={1}
                  direction="column"
                  gap="xxxs"
                  onClick={() => handleSelectItem(item)}
                  style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                >
                  <Text size="textMd" fw={FONT_WEIGHTS.medium} c="gray.7">
                    {item.title}
                  </Text>
                  {item.description && (
                    <Text size="textMd" fw={FONT_WEIGHTS.regular} c="gray.6">
                      {item.description}
                    </Text>
                  )}
                </Flex>
                <Text size={'sm'} c={'gray.6'}>
                  {item.right}
                </Text>
              </Flex>
            </Flex>
          );
          return (
            <Flex
              data-testid="selectable-list-item"
              key={item.id}
              direction="column"
              gap="xs"
              role={'listitem'}
            >
              {isDisabled && item.disabledReason ? (
                <Tooltip label={item.disabledReason} multiline w={260} withArrow>
                  {row}
                </Tooltip>
              ) : (
                row
              )}
              {index < items.length - 1 && <Divider />}
            </Flex>
          );
        })}
      </Flex>
      {hint && items.length > 0 && (
        <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6" py="xs" px="sm">
          {hint}
        </Text>
      )}
    </Flex>
  );
}
